import { Box, Drawer, useMediaQuery } from '@mui/material';
import { CSSObject, Theme, styled, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';

import { useNav } from '../../../hooks/useNav';
import { DrawerProps } from '../../../types/types';
import DrawerContent from './DrawerContent';
import DrawerHeader from './DrawerHeader';

const MainDrawer = ({ window, logo, logoClosed, appTitle }: DrawerProps) => {
  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down('lg'));

  const { setDrawerOpen, drawerOpen } = useNav();

  // responsive drawer container
  const container =
    window !== undefined ? () => window().document.body : undefined;

  const drawerContent = useMemo(() => <DrawerContent />, []);

  const drawerHeader = useMemo(
    () => (
      <DrawerHeader
        open={drawerOpen}
        logo={logo}
        logoClosed={logoClosed}
        appTitle={appTitle}
        drawerOpen={drawerOpen}
      />
    ),
    [drawerOpen, logo, logoClosed, appTitle]
  );

  return (
    <Box component="nav" sx={{ flexShrink: { md: 0 }, zIndex: 1200 }}>
      {!matchDownMD ? (
        <MiniDrawerStyled variant="permanent" open={drawerOpen}>
          {drawerHeader}
          {drawerContent}
        </MiniDrawerStyled>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(!drawerOpen)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: theme.sidebar.width,
              borderRight:
                theme.sidebar.appearance === 'dark'
                  ? 'none'
                  : `1px solid ${theme.palette.divider}`,
              backgroundImage: 'none',
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.sidebar.background.dark
                  : theme.sidebar.background.default,
              boxShadow: 'inherit',
            },
          }}
        >
          {drawerHeader}
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

interface MiniDrawerStyledProps {
  open: boolean;
}

const openedMixin = (theme: Theme): CSSObject => ({
  width: theme.sidebar.width,
  borderRight:
    theme.sidebar.appearance === 'dark'
      ? 'none'
      : `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.sidebar.background.dark
      : theme.sidebar.background.default,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: theme.sidebar.closedWidth,
  borderRight: 'none',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.sidebar.background.dark
      : theme.sidebar.background.default,
  boxShadow:
    theme.sidebar.appearance === 'dark'
      ? '4px 0 16px 0 rgba(0, 0, 0, 0.25)'
      : theme.shadows[1],
});

const MiniDrawerStyled = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})<MiniDrawerStyledProps>(({ theme, open }) => ({
  width: theme.sidebar.width,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

export default MainDrawer;
